import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Masthead from "../components/Masthead";
import { api, setAdminToken, loadAdminToken } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, KeyRound } from "lucide-react";

function LoginView({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password });
      setAdminToken(r.data.token);
      toast.success("Welcome, editor.");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-6" data-testid="admin-login-view">
      <div className="rule-thick" />
      <div className="bg-white border-l-2 border-r-2 border-b-2 border-[color:var(--ink-navy)] p-8">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound size={18} />
          <span className="font-mono-plex text-[11px] uppercase tracking-[0.3em]">Editor Access</span>
        </div>
        <h2 className="font-serif-display text-4xl">Editor's Desk</h2>
        <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
          Enter the newsroom passphrase to set today's puzzle.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passphrase"
            className="w-full border-2 border-[color:var(--ink-navy)] px-3 py-3 font-mono-plex text-sm bg-transparent outline-none focus:bg-[color:var(--paper)]"
            data-testid="admin-password-input"
            autoFocus
          />
          <button type="submit" className="btn-editorial w-full" disabled={busy} data-testid="admin-login-submit">
            {busy ? "Verifying…" : "Enter Newsroom"}
          </button>
        </form>

        <div className="rule-thin my-6" />
        <Link to="/" className="font-mono-plex text-[11px] uppercase tracking-widest underline underline-offset-4" style={{ color: "var(--muted)" }}>
          ← Back to the paper
        </Link>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);

  const todayIso = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState("");
  const [answer, setAnswer] = useState("");
  const [maxGuesses, setMaxGuesses] = useState(6);
  const [hint, setHint] = useState("");
  const [byline, setByline] = useState("");
  const [publishDate, setPublishDate] = useState(todayIso);
  const [correctColor, setCorrectColor] = useState("#6B8E73");
  const [presentColor, setPresentColor] = useState("#D4AF37");
  const [busy, setBusy] = useState(false);

  const PRESETS = [
    { name: "Sagebrush", correct: "#6B8E73", present: "#D4AF37" },
    { name: "Homecoming", correct: "#0A2540", present: "#C5A253" },
    { name: "Winter", correct: "#3E6D8E", present: "#B8D0DB" },
    { name: "Harvest", correct: "#8B4513", present: "#D97706" },
    { name: "Wolf Pack", correct: "#003F5C", present: "#B8860B" },
  ];

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/puzzles");
      setPuzzles(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Could not load puzzles");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const clean = answer.trim().toUpperCase();
    if (!/^[A-Z]{4,8}$/.test(clean)) {
      toast.error("Answer must be 4-8 letters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/puzzles", {
        title: title.trim() || `Puzzle · ${new Date().toLocaleDateString()}`,
        answer: clean,
        max_guesses: Number(maxGuesses),
        hint,
        byline,
        publish_date: publishDate,
        correct_color: correctColor,
        present_color: presentColor,
      });
      toast.success("Puzzle set for print.");
      setTitle(""); setAnswer(""); setHint(""); setByline("");
      setMaxGuesses(6); setPublishDate(todayIso);
      setCorrectColor("#6B8E73"); setPresentColor("#D4AF37");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create puzzle");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Pull this puzzle from the press?")) return;
    try {
      await api.delete(`/admin/puzzles/${id}`);
      toast.success("Puzzle removed.");
      refresh();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif-display text-4xl">Editor's Desk</h2>
        <button className="btn-editorial ghost flex items-center gap-2" onClick={onLogout} data-testid="admin-logout">
          <LogOut size={14} /> Log Out
        </button>
      </div>
      <div className="rule-thick mb-6" />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Create form */}
        <section className="lg:col-span-2">
          <div className="border-2 border-[color:var(--ink-navy)] bg-white p-6" data-testid="create-puzzle-card">
            <div className="flex items-center gap-2 mb-1">
              <Plus size={16} />
              <span className="font-mono-plex text-[11px] uppercase tracking-[0.3em]">Set Today's Word</span>
            </div>
            <h3 className="font-serif-display text-2xl mb-4">Compose a Puzzle</h3>
            <form onSubmit={create} className="space-y-3">
              <div>
                <label className="font-mono-plex text-[11px] uppercase tracking-widest">Title / Headline</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Homecoming Edition"
                  className="w-full border-2 border-[color:var(--ink-navy)] px-3 py-2 mt-1 font-mono-plex text-sm bg-transparent outline-none"
                  data-testid="new-puzzle-title"
                />
              </div>
              <div>
                <label className="font-mono-plex text-[11px] uppercase tracking-widest">Answer (4–8 letters)</label>
                <input
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase())}
                  maxLength={8}
                  placeholder="WOLFPACK"
                  className="w-full border-2 border-[color:var(--ink-navy)] px-3 py-2 mt-1 font-mono-plex text-lg tracking-widest bg-transparent outline-none uppercase"
                  data-testid="new-puzzle-answer"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-plex text-[11px] uppercase tracking-widest">Max Guesses</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={maxGuesses}
                    onChange={(e) => setMaxGuesses(e.target.value)}
                    className="w-full border-2 border-[color:var(--ink-navy)] px-3 py-2 mt-1 font-mono-plex text-sm bg-transparent outline-none"
                    data-testid="new-puzzle-guesses"
                  />
                </div>
                <div>
                  <label className="font-mono-plex text-[11px] uppercase tracking-widest">Word Length</label>
                  <div className="border-2 border-[color:var(--ink-navy)] px-3 py-2 mt-1 font-mono-plex text-sm bg-[color:var(--paper)]">
                    {answer.length || "—"}
                  </div>
                </div>
              </div>
              <div>
                <label className="font-mono-plex text-[11px] uppercase tracking-widest">Hint (optional)</label>
                <input
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  placeholder="e.g. UNR mascot"
                  className="w-full border-2 border-[color:var(--ink-navy)] px-3 py-2 mt-1 font-mono-plex text-sm bg-transparent outline-none"
                  data-testid="new-puzzle-hint"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-plex text-[11px] uppercase tracking-widest">Publish Date</label>
                  <input
                    type="date"
                    value={publishDate}
                    onChange={(e) => setPublishDate(e.target.value)}
                    className="w-full border-2 border-[color:var(--ink-navy)] px-3 py-2 mt-1 font-mono-plex text-sm bg-transparent outline-none"
                    data-testid="new-puzzle-date"
                  />
                </div>
                <div>
                  <label className="font-mono-plex text-[11px] uppercase tracking-widest">Byline (optional)</label>
                  <input
                    value={byline}
                    onChange={(e) => setByline(e.target.value)}
                    placeholder="e.g. Jane Doe, Editor"
                    className="w-full border-2 border-[color:var(--ink-navy)] px-3 py-2 mt-1 font-mono-plex text-sm bg-transparent outline-none"
                    data-testid="new-puzzle-byline"
                  />
                </div>
              </div>
              <div>
                <label className="font-mono-plex text-[11px] uppercase tracking-widest">Palette</label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {PRESETS.map((preset) => {
                    const active = correctColor === preset.correct && presentColor === preset.present;
                    return (
                      <button
                        type="button"
                        key={preset.name}
                        onClick={() => { setCorrectColor(preset.correct); setPresentColor(preset.present); }}
                        className={`px-3 py-1.5 border-2 font-mono-plex text-[10px] uppercase tracking-widest flex items-center gap-2
                          ${active ? "bg-[color:var(--ink-navy)] text-white border-[color:var(--ink-navy)]" : "border-[color:var(--ink-navy)] text-[color:var(--ink-navy)]"}`}
                        data-testid={`preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className="flex gap-1">
                          <span className="w-3 h-3 border border-black" style={{ background: preset.correct }} />
                          <span className="w-3 h-3 border border-black" style={{ background: preset.present }} />
                        </span>
                        {preset.name}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <label className="flex items-center gap-2 border-2 border-[color:var(--ink-navy)] px-3 py-2">
                    <input
                      type="color"
                      value={correctColor}
                      onChange={(e) => setCorrectColor(e.target.value)}
                      className="w-8 h-8 cursor-pointer bg-transparent border-0 p-0"
                      data-testid="new-puzzle-correct-color"
                    />
                    <span className="font-mono-plex text-[10px] uppercase tracking-widest">Correct</span>
                  </label>
                  <label className="flex items-center gap-2 border-2 border-[color:var(--ink-navy)] px-3 py-2">
                    <input
                      type="color"
                      value={presentColor}
                      onChange={(e) => setPresentColor(e.target.value)}
                      className="w-8 h-8 cursor-pointer bg-transparent border-0 p-0"
                      data-testid="new-puzzle-present-color"
                    />
                    <span className="font-mono-plex text-[10px] uppercase tracking-widest">Present</span>
                  </label>
                </div>
                <div className="flex gap-1 mt-3" data-testid="palette-preview">
                  {["S","A","G","E"].map((c, i) => (
                    <div
                      key={i}
                      className="w-9 h-9 flex items-center justify-center border-2 font-semibold text-sm text-white"
                      style={{
                        background: i === 0 || i === 3 ? correctColor : i === 2 ? presentColor : "var(--slate)",
                        borderColor: i === 0 || i === 3 ? correctColor : i === 2 ? presentColor : "var(--slate)",
                      }}
                    >
                      {c}
                    </div>
                  ))}
                </div>
              </div>
              <button type="submit" className="btn-editorial w-full" disabled={busy} data-testid="new-puzzle-submit">
                {busy ? "Setting…" : "Publish Puzzle"}
              </button>
            </form>
          </div>
        </section>

        {/* Puzzle list */}
        <section className="lg:col-span-3">
          <div className="border-2 border-[color:var(--ink-navy)] bg-white" data-testid="admin-puzzle-list">
            <div className="p-4 border-b-2 border-[color:var(--ink-navy)] flex items-center justify-between">
              <h3 className="font-serif-display text-2xl">In Circulation</h3>
              <span className="font-mono-plex text-xs uppercase tracking-widest" style={{ color: "var(--muted)" }}>
                {puzzles.length} total
              </span>
            </div>
            {loading ? (
              <div className="p-6 font-mono-plex text-sm" style={{ color: "var(--muted)" }}>Loading…</div>
            ) : puzzles.length === 0 ? (
              <div className="p-8 text-center">
                <p className="font-serif-display text-2xl">The desk is clear.</p>
                <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Set your first puzzle to the left.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono-plex text-[10px] uppercase tracking-widest" style={{ color: "var(--muted)" }}>
                    <th className="p-3 border-b border-[color:var(--ink-navy)]">Title</th>
                    <th className="p-3 border-b border-[color:var(--ink-navy)]">Answer</th>
                    <th className="p-3 border-b border-[color:var(--ink-navy)]">Byline</th>
                    <th className="p-3 border-b border-[color:var(--ink-navy)]">Pub</th>
                    <th className="p-3 border-b border-[color:var(--ink-navy)]">Guesses</th>
                    <th className="p-3 border-b border-[color:var(--ink-navy)]">Palette</th>
                    <th className="p-3 border-b border-[color:var(--ink-navy)]"></th>
                  </tr>
                </thead>
                <tbody>
                  {puzzles.map((p) => (
                    <tr key={p.id} className="border-b border-[color:var(--border-ink)]" data-testid={`admin-row-${p.id}`}>
                      <td className="p-3 font-serif-display text-lg">{p.title}</td>
                      <td className="p-3 font-mono-plex tracking-widest">{p.answer}</td>
                      <td className="p-3 font-mono-plex text-xs" style={{ color: "var(--muted)" }}>{p.byline || "—"}</td>
                      <td className="p-3 font-mono-plex text-xs">{p.publish_date || (p.created_at || "").slice(0,10)}</td>
                      <td className="p-3 font-mono-plex">{p.max_guesses}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <span className="w-4 h-4 border border-black" style={{ background: p.correct_color || "#6B8E73" }} />
                          <span className="w-4 h-4 border border-black" style={{ background: p.present_color || "#D4AF37" }} />
                        </div>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => remove(p.id)}
                          className="inline-flex items-center gap-1 text-xs uppercase font-mono-plex tracking-widest hover:text-red-700"
                          data-testid={`delete-${p.id}`}
                        >
                          <Trash2 size={14} /> Pull
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const t = loadAdminToken();
    if (!t) { setChecking(false); return; }
    api.get("/admin/verify")
      .then(() => setAuthed(true))
      .catch(() => setAdminToken(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch {}
    setAdminToken(null);
    setAuthed(false);
    navigate("/admin");
  };

  return (
    <div className="paper-texture min-h-screen">
      <Masthead />
      <main className="relative z-10 px-6 pb-24">
        {checking ? (
          <p className="text-center font-mono-plex text-sm mt-16" style={{ color: "var(--muted)" }}>Checking credentials…</p>
        ) : authed ? (
          <Dashboard onLogout={logout} />
        ) : (
          <LoginView onSuccess={() => setAuthed(true)} />
        )}
      </main>
    </div>
  );
}
